// Customers Management Page - Clean & Minimal

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '@/lib/api';
import type { Customer, CustomerFormData } from '@/lib/types';
import Button from '@/components/ui/Button';
import CustomerList from '@/components/customers/CustomerList';
import CustomerForm from '@/components/customers/CustomerForm';
import InviteCustomerModal from '@/components/customers/InviteCustomerModal';
import Modal from '@/components/ui/Modal';
import toast from 'react-hot-toast';

export default function CustomersPage() {
  const queryClient = useQueryClient();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [inviteCustomer, setInviteCustomer] = useState<Customer | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [deleteCustomerModal, setDeleteCustomerModal] = useState<Customer | null>(null);

  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });

  const createMutation = useMutation({
    mutationFn: (data: CustomerFormData) => createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Kunde erfolgreich erstellt');
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Erstellen des Kunden');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomerFormData> }) =>
      updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Kunde erfolgreich aktualisiert');
      setIsModalOpen(false);
      setEditingCustomer(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Aktualisieren des Kunden');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Kunde erfolgreich gelöscht');
      setDeleteCustomerModal(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Löschen des Kunden');
    },
  });

  const handleCreate = (data: CustomerFormData) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (data: CustomerFormData) => {
    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data });
    }
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleNewCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleInvite = (customer: Customer) => {
    setInviteCustomer(customer);
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteCustomer(null);
  };

  const handleDelete = (customer: Customer) => {
    setDeleteCustomerModal(customer);
  };

  const confirmDelete = () => {
    if (deleteCustomerModal) {
      deleteMutation.mutate(deleteCustomerModal.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Kunden</h1>
          <p className="text-neutral-500 mt-1">
            {customers?.length || 0} Kunden
          </p>
        </div>

        <Button onClick={handleNewCustomer}>
          + Neuer Kunde
        </Button>
      </div>

      {/* Customers List */}
      <CustomerList
        customers={customers || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onInvite={handleInvite}
        onDelete={handleDelete}
        onToggleActive={(customer) =>
          updateMutation.mutate({
            id: customer.id,
            data: { active: !customer.active },
          })
        }
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
      >
        <CustomerForm
          customer={editingCustomer}
          onSubmit={editingCustomer ? handleUpdate : handleCreate}
          onCancel={handleCloseModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Invite Customer Modal */}
      <InviteCustomerModal
        customer={inviteCustomer}
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteCustomerModal}
        onClose={() => setDeleteCustomerModal(null)}
        title="Kunde löschen"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="font-medium text-red-800">
              Möchten Sie "{deleteCustomerModal?.name}" wirklich löschen?
            </p>
            <p className="mt-2 text-sm text-red-700">
              Diese Aktion kann nicht rückgängig gemacht werden. Folgende Daten werden ebenfalls gelöscht:
            </p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              <li>Alle Projekte dieses Kunden</li>
              <li>Alle Anfragen (Leads) dieses Kunden</li>
              <li>Alle verknüpften Benutzerkonten</li>
            </ul>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => setDeleteCustomerModal(null)}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              loading={deleteMutation.isPending}
            >
              Endgültig löschen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
